const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const matchSlidesKey = Object.keys(carousel.container.dataset).find(
      key => carousel.container.dataset[key] === 'slides',
    )
    const carouselName = matchSlidesKey
      ? matchSlidesKey
        .replace('El', '')
        .replace(/[A-Z]/g, ' $&')
        .trim()
        .toLowerCase()
      : carousel.container.className

    carousel.on('created', carousel => {
      carousel.container.setAttribute(`data-initialised`, true)
      window.BAO.dispatchEvent(window.BAO.EVENTS.CAROUSEL.CREATED)
    })
    carousel.on('slideChanged', carousel => {
      window.dataLayer.push({
        page_type: 'carousel',
        event: 'carousel_engagement',
        carousel_name: carouselName,
      })

      const { abs, maxIdx } = carousel.track.details

      if (abs === maxIdx) {
        window.dataLayer.push({
          page_type: 'carousel',
          event: 'carousel_engagement_end',
          carousel_name: carouselName,
        })
      }
    })

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return

          window.dataLayer.push({
            page_type: 'carousel',
            event: 'carousel_impression',
            carousel_name: carouselName,
          })

          observer.unobserve(entry.target)
        })
      },
      {
        threshold: 0.8,
      },
    )
    observer.observe(carousel.container)
  }
}

/**
 * @param {{
 *  container?: HTMLElement,
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} arrows
 */
const useArrows = ({ container, next, previous, hideArrows = false } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', () => carousel.next())
    previous?.addEventListener('click', () => carousel.prev())

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      container?.setAttribute('aria-hidden', maxIdx === 0)

      if (next) {
        if (hideArrows) {
          console.log('hideArrows next', abs, maxIdx)
          const isAtEnd = abs === maxIdx
          next.setAttribute('aria-hidden', isAtEnd)
          next.tabIndex = isAtEnd ? -1 : 0
        } else {
          next.disabled = abs === maxIdx
        }
      }

      console.log({
        previous,
        hideArrows,
      })

      if (previous) {
        if (hideArrows) {
          console.log('hideArrows previous', abs, min)
          const isAtStart = abs === min // Corrected line
          previous.setAttribute('aria-hidden', isAtStart)
          previous.tabIndex = isAtStart ? -1 : 0
        } else {
          previous.disabled = abs === min
        }
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

/**
 * @param {{
 *  container?: HTMLElement,
 *  progressbar?: HTMLElement,
 * }} progress
 */
const useProgress = ({ container, progressbar } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const updateProgressState = () => {
      const maximumIndex = carousel.track.details.maxIdx
      const currentIndex = carousel.track.details.rel

      container?.setAttribute('aria-hidden', maximumIndex === 0)

      const barLength = 100 / (maximumIndex + 1)
      const barPositon = (currentIndex / (maximumIndex + 1)) * 100

      progressbar?.style.setProperty('--Bar_Width', `${barLength}%`)
      progressbar?.style.setProperty('--Bar_Position', `${barPositon}%`)
    }

    carousel.on('created', updateProgressState)
    carousel.on('slideChanged', updateProgressState)
  }
}

/**
 * @param {HTMLElement[]} slides
 * @param {boolean} [mobileOnly]
 */
const useReveal = (slides = [], mobileOnly = false) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', () => {
      const revealClasses = ['util-Slide_Reveal']

      if (mobileOnly === true) {
        revealClasses.push('util-Slide_Reveal-mobile')
      }

      const slideReveal = () => {
        if (slides.length < 2) return

        slides[0].classList.add(...revealClasses)
        slides[1].classList.add(...revealClasses)

        slides[0].addEventListener(
          'animationend',
          () => {
            slides[0].classList.remove(...revealClasses)
            slides[1].classList.remove(...revealClasses)
          },
          { once: true },
        )
      }

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return

            slideReveal()
            observer.unobserve(entry.target)
          })
        },
        {
          rootMargin: '0px',
          threshold: 0.5,
        },
      )

      observer.observe(carousel.container)
    })
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useArrows,
  useAutoplay,
  useDots,
  useProgress,
  useReveal,
}
